<template>
    <section>
        <div :class="$style.cards">
            <CardCarousel
                v-for="(group, groupIndex) in props.rewards"
                :key="groupIndex"
                :class="$style.cards_group"
                :items="group"
            >
                <template #default="{ item, className, bigCardClassName }">
                    <div :class="[$style.cards_item, className, bigCardClassName]">
                        <RewardCard
                            :type="item.type"
                            :value="item.value"
                            :currency="item.currency"
                            :class="$style.card"
                            @click="onCardClick(item)"
                        />
                        <Typography
                            :class="[$style.card_availability, $style['card_availability_' + item.availability]]"
                            size="s"
                            accent
                        >
                            {{ '● ' + availabilityLabels[item.availability] }}
                        </Typography>
                        <Button
                            :class="$style.card_button"
                            :interfaceTarget="getPurchaseInterfaceTarget(item)"
                            iconLeft="silver_fennec_coin_small"
                            size="l"
                            color="purple-graphite"
                            width="full"
                            @click="onCardClick(item)"
                        >
                            {{ formatValue(item.price) }}
                        </Button>
                    </div>
                </template>
            </CardCarousel>
        </div>
        <RewardPurchaseModal
            :purchase="purchaseDialogState"
            :doPurchase="props.purchaseReward"
            @closeModal="purchaseDialogState = undefined"
        />
    </section>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import type { RewardCardType, RewardPurchasePayload } from '@/types'
import { useTr } from '@/composables/use-tr'
import Typography from '@/components_new/Typography.vue'
import Button from '@/components_new/Button.vue'
import CardCarousel from '@/components_new/CardCarousel.vue'
import { formatValue } from '@/utils/numbers'
import RewardCard from './RewardCard.vue'
import { useUserStore } from '@/store/user-store/user-store'
import { useLayoutStore } from '@/store/layout-store'
import RewardPurchaseModal from './RewardPurchaseModal.vue'

const props = defineProps<{
    rewards: RewardCardType[][]
    purchaseReward(payload: RewardPurchasePayload): Promise<void>
}>()

const tr = useTr()

const availabilityLabels = computed(() => ({
    low: tr('rewards.availability_low'),
    medium: tr('rewards.availability_medium'),
    high: tr('rewards.availability_high'),
}))

const userStore = useUserStore()
const layoutStore = useLayoutStore()
const purchaseDialogState = ref<
    { card: RewardCardType; email: string; country: { code: string; nameEn: string } } | undefined
>(undefined)

function getPurchaseInterfaceTarget(item: RewardCardType) {
    if (!userStore.isAuthorized) {
        return 'open-sign-in-modal'
    }
    if (userStore.userBalance < item.price) {
        return 'open-need-more-fennecs-modal'
    }
    return 'open-purchase-modal'
}

function onCardClick(item: RewardCardType) {
    if (userStore.isAuthorized) {
        if (userStore.userBalance >= item.price) {
            const locale = window.additionalServerData?.locale
            purchaseDialogState.value = {
                card: item,
                email: userStore.user?.email || 'unknown email', // TODO i18n, goes to Backend and UI,
                country: {
                    code: locale?.countryCode || 'unknown-country', // goes to Backend, no need for i18n
                    nameEn: locale?.countryNameEn || tr('rewards.purchase_dialog_unknown_country'),
                },
            }
        } else {
            layoutStore.setInviteModalOpened('need_more_fennecs')
        }
    } else {
        layoutStore.setSignInModalOpened(true)
    }
}
</script>

<style module>
.cards_item {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.card {
    cursor: pointer;
}

.card_availability_low {
    color: var(--rose-400);
}
.card_availability_medium {
    color: var(--golden-300);
}
.card_availability_high {
    color: var(--experimental-salad);
}

@media (--mobile) {
    .cards {
        display: flex;
        flex-direction: column;
        gap: 32px;
    }
}

@media (--tablet-plus) {
    .cards {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 24px 12px;
    }

    .cards_group,
    .cards_group *:not(.cards_item):not(.cards_item *) {
        display: contents;
    }

    .cards_item {
        grid-column: auto;
        grid-row: auto;
    }
}

@media (--desktop-medium-plus) {
    .cards {
        grid-template-columns: repeat(3, 1fr);
    }
}
</style>
